import { createSlice } from "@reduxjs/toolkit"
import { isDateExpired } from "helpers/isDateExpired"
import { ServerDate } from "helpers/server-date.class.ts"
import { calculateAdditionalProgress } from "helpers/calcAdditionalProgress.ts"
import {
  getAllLocations,
  getLocationUserProgress,
  startDigging,
  skipCycle
} from "../apis/locationAPI/location.api.ts"

interface MiningState {
  locations: {
    list: ILocation[]
    current: ILocation
    currentIndex: number
  }
  time: {
    localTimeOnInit: number | null
    startTime: number | null
    finishTime: number | null
  }
  usedAutoRecyclesForCurrentCycle: number
  // Per-cycle ms sealed at dig start. Null when the value can't be derived from
  // backend payload alone (isLastCycle && usedAutoRecycles > 0) — the consumer
  // snapshots it from live stamina in that case.
  cycleTimeMs: number | null
  speed: number // this speed value is frozen when mining is started
  adjustedSpeed: number // digging speed for current (or next) mining cycle with temporary boost like speed elixir
  latestSpeed: number // this speed is updated even if mining is started
  mined: number
  minedOnInit: number
  reward: {
    location: {
      bones: number
      gold: number
      xp: number
    }
    cycle: {
      bones: number
      gold: number
    }
  }
  isMining: boolean
  isSleeping: boolean
  isLevelFinished: boolean
}

const initialState: MiningState = {
  locations: {
    list: [
      {
        reward: {
          bones: 100,
          gold: 5,
          xp: 1
        },
        images: {
          bg: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/soil.svg",
          pit: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/soil-pit.svg"
        },
        _id: "672e0e4eb158ed3d1f872794",
        index: 1,
        name: "soil",
        description: "",
        metres: 1,
        farmingTime: 120,
        diggingSpeed: 1,
        seasonReward: [
          {
            position: 1,
            labrAmount: 20000
          },
          {
            position: 2,
            labrAmount: 10000
          },
          {
            position: 3,
            labrAmount: 4000
          },
          {
            position: 4,
            labrAmount: 2000
          },
          {
            position: 5,
            labrAmount: 2000
          },
          {
            position: 6,
            labrAmount: 1000
          },
          {
            position: 7,
            labrAmount: 1000
          },
          {
            position: 8,
            labrAmount: 200
          },
          {
            position: 9,
            labrAmount: 200
          },
          {
            position: 10,
            labrAmount: 200
          }
        ],
        //diggingProgress: [],
        locationFinalPoint: 1
      },
      {
        reward: {
          bones: 1000,
          gold: 10,
          xp: 1
        },
        images: {
          bg: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/sandstone.svg",
          pit: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/sandstone-pit.svg"
        },
        _id: "672e0e4eb158ed3d1f872795",
        index: 2,
        name: "sandstone",
        description: "",
        metres: 4,
        farmingTime: 180,
        diggingSpeed: 1,
        seasonReward: [
          {
            position: 1,
            labrAmount: 20000
          },
          {
            position: 2,
            labrAmount: 10000
          },
          {
            position: 3,
            labrAmount: 4000
          },
          {
            position: 4,
            labrAmount: 2000
          },
          {
            position: 5,
            labrAmount: 2000
          },
          {
            position: 6,
            labrAmount: 1000
          },
          {
            position: 7,
            labrAmount: 1000
          },
          {
            position: 8,
            labrAmount: 200
          },
          {
            position: 9,
            labrAmount: 200
          },
          {
            position: 10,
            labrAmount: 200
          }
        ],
        //diggingProgress: [],
        locationFinalPoint: 5
      },
      {
        reward: {
          bones: 3000,
          gold: 25,
          xp: 1
        },
        images: {
          bg: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/limestone.svg",
          pit: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/limestone-pit.svg"
        },
        _id: "672e0e4eb158ed3d1f872796",
        index: 3,
        name: "limestone",
        description: "",
        metres: 15,
        farmingTime: 200,
        diggingSpeed: 1,
        seasonReward: [
          {
            position: 1,
            labrAmount: 20000
          },
          {
            position: 2,
            labrAmount: 10000
          },
          {
            position: 3,
            labrAmount: 4000
          },
          {
            position: 4,
            labrAmount: 2000
          },
          {
            position: 5,
            labrAmount: 2000
          },
          {
            position: 6,
            labrAmount: 1000
          },
          {
            position: 7,
            labrAmount: 1000
          },
          {
            position: 8,
            labrAmount: 200
          },
          {
            position: 9,
            labrAmount: 200
          },
          {
            position: 10,
            labrAmount: 200
          }
        ],
        //diggingProgress: [],
        locationFinalPoint: 20
      }
    ],
    current: {
      reward: {
        bones: 1000,
        gold: 10,
        xp: 1
      },
      images: {
        bg: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/sandstone.svg",
        pit: "https://labrador-prj.s3.us-east-2.amazonaws.com/location/sandstone-pit.svg"
      },
      _id: "672e0e4eb158ed3d1f872795",
      index: 2,
      name: "sandstone",
      description: "",
      metres: 4,
      farmingTime: 180,
      diggingSpeed: 1,
      seasonReward: [
        {
          position: 1,
          labrAmount: 20000
        },
        {
          position: 2,
          labrAmount: 10000
        },
        {
          position: 3,
          labrAmount: 4000
        },
        {
          position: 4,
          labrAmount: 2000
        },
        {
          position: 5,
          labrAmount: 2000
        },
        {
          position: 6,
          labrAmount: 1000
        },
        {
          position: 7,
          labrAmount: 1000
        },
        {
          position: 8,
          labrAmount: 200
        },
        {
          position: 9,
          labrAmount: 200
        },
        {
          position: 10,
          labrAmount: 200
        }
      ],
      //diggingProgress: [],
      locationFinalPoint: 5
    },
    currentIndex: 1
  },
  time: {
    localTimeOnInit: null,
    startTime: null,
    finishTime: null
  },
  usedAutoRecyclesForCurrentCycle: 0,
  cycleTimeMs: null,
  speed: 3000,
  adjustedSpeed: 3000, // digging speed for current (or next) mining cycle with temporary boost like speed elixir
  latestSpeed: 3000,
  mined: 1,
  minedOnInit: 1,
  reward: {
    location: {
      bones: 0,
      gold: 0,
      xp: 1
    },
    cycle: {
      bones: 0,
      gold: 0
    }
  },
  isMining: false,
  isSleeping: false,
  isLevelFinished: false
}

export const miningSlice = createSlice({
  name: "mining",
  initialState,
  reducers: {
    levelUp: (state) => {
      if (state.locations.currentIndex < state.locations.list.length - 1) {
        state.locations.currentIndex += 1
        state.locations.current =
          state.locations.list[state.locations.currentIndex]
      }
    },

    updateMiningState: (state, { payload }) => {
      const { isMining, isSleeping, isLevelFinished } = payload
      state.isMining = isMining
      state.isSleeping = isSleeping
      state.isLevelFinished = isLevelFinished
    },

    setMiningSpeedValue: (state, { payload }) => {
      if (!state.isMining) {
        state.speed = +payload.toFixed(2)
        state.adjustedSpeed = +payload.toFixed(2)
      }
      state.latestSpeed = +payload.toFixed(2)
    },

    mineDown: (state) => {
      const additionalProgress = calculateAdditionalProgress(
        state.time.finishTime,
        state.time.startTime,
        state.adjustedSpeed
      )

      const newProgressValue = Number(
        Number(state.minedOnInit + additionalProgress).toFixed(4)
      )
      const currentLocationBottom =
        state?.locations?.current?.locationFinalPoint

      state.mined =
        newProgressValue > currentLocationBottom
          ? currentLocationBottom
          : newProgressValue
    },

    finishMining: (state) => {
      state.time.startTime = null
      state.time.finishTime = null
      state.cycleTimeMs = null
    },

    setCycleTimeMsSnapshot: (state, { payload }) => {
      state.cycleTimeMs = payload
    }
  },

  extraReducers: (builder) => {
    builder.addMatcher(getAllLocations.matchFulfilled, (state, { payload }) => {
      state.locations.list = payload
    })

    builder.addMatcher(
      getLocationUserProgress.matchFulfilled,
      (state, { payload }) => {
        state.locations.currentIndex = payload.locationIndex - 1
        state.locations.current = state.locations.list.find(
          (loc) => loc._id === payload.locationId
        )!

        state.usedAutoRecyclesForCurrentCycle = payload.usedAutoRecycles

        const currentDepth =
          state?.locations?.current?.locationFinalPoint -
          state.locations.current?.metres +
          payload.progressValue

        state.speed = payload.diggingSpeed
        state.adjustedSpeed = payload.adjustedDiggingSpeed

        state.reward = {
          location: payload.box,
          cycle: {
            ...payload.incomePerCycle,
            xp: payload.usedAutoRecycles + 1
          }
        }

        state.time.startTime = new ServerDate(payload.startTime).getTime()
        state.time.finishTime = new ServerDate(payload.finishTime).getTime()

        const totalSpan =
          (state.time.finishTime ?? 0) - (state.time.startTime ?? 0)
        const n = payload.usedAutoRecycles ?? 0
        if (!payload.isLastCycle) {
          // All (n+1) cycles equal length.
          state.cycleTimeMs = totalSpan > 0 ? totalSpan / (n + 1) : null
        } else if (n === 0) {
          // Single (possibly partial) cycle — bar tracks the whole dig.
          state.cycleTimeMs = totalSpan > 0 ? totalSpan : null
        } else {
          // n equal cycles + 1 partial last — can't derive from totals alone.
          // The consumer (useMine) snapshots it from live stamina.
          state.cycleTimeMs = null
        }

        const additionalProgress = calculateAdditionalProgress(
          state.time.finishTime,
          state.time.startTime,
          state.adjustedSpeed
        )

        state.mined = currentDepth + additionalProgress
        state.minedOnInit = currentDepth

        const isMiningNow = () => {
          const finishTime = state.time.finishTime
          const currentTime = new ServerDate().getTime()

          return !!(finishTime && finishTime > currentTime)
        }

        const isInTheBeginningOfLocation = () => {
          const previousLocationDepth =
            state.locations.list?.[state.locations.currentIndex - 1]
              ?.locationFinalPoint
          // We are at the beginning if current mined values == previousLocationDepth
          // or payload.totalMetersPassed === 0 (which means that user only started the game)
          return (
            payload.totalMetersPassed === previousLocationDepth ||
            payload.totalMetersPassed === 0
          )
        }

        const isLocationFinished =
          state.mined >= state.locations.current?.locationFinalPoint
        const hasCycleReward =
          payload.incomePerCycle?.bones > 0 || payload.incomePerCycle?.gold > 0

        state.isMining = isMiningNow()
        state.isSleeping =
          !isMiningNow() &&
          !isInTheBeginningOfLocation() &&
          !isLocationFinished &&
          hasCycleReward
        state.isLevelFinished = isLocationFinished
      }
    )

    builder.addMatcher(startDigging.matchFulfilled, (state, { payload }) => {
      state.time.startTime = new ServerDate(
        payload.userProgress.startTime
      ).getTime()
      state.time.finishTime = new ServerDate(
        payload.userProgress.finishTime
      ).getTime()

      state.minedOnInit = state.mined

      state.isMining = true
      state.isSleeping = false
      state.isLevelFinished = false

      state.usedAutoRecyclesForCurrentCycle =
        payload.userProgress.usedAutoRecycles

      const totalSpan =
        (state.time.finishTime ?? 0) - (state.time.startTime ?? 0)
      const n = payload.userProgress.usedAutoRecycles ?? 0
      if (!payload.userProgress.isLastCycle) {
        state.cycleTimeMs = totalSpan > 0 ? totalSpan / (n + 1) : null
      } else if (n === 0) {
        state.cycleTimeMs = totalSpan > 0 ? totalSpan : null
      } else {
        state.cycleTimeMs = null
      }
    })

    builder.addMatcher(skipCycle.matchFulfilled, (state, { payload }) => {
      state.time.startTime = new ServerDate(payload.startTime).getTime()
      state.time.finishTime = new ServerDate(payload.finishTime).getTime()
      state.isMining = !isDateExpired(state.time.finishTime)
    })
  }
})

export const {
  levelUp,
  updateMiningState,
  mineDown,
  finishMining,
  setMiningSpeedValue,
  setCycleTimeMsSnapshot
} = miningSlice.actions

export default miningSlice
